import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  AccordionToggleEventHandler,
  Button,
  makeStyles,
  tokens,
  Drawer,
  DrawerHeaderTitle,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowClockwiseDashesRegular,
  ArrowSyncCheckmarkRegular,
  BuildingRegular,
  ChartMultipleRegular,
  LineHorizontal3Regular,
  SettingsRegular,
  ViewDesktopRegular,
  ArrowTrendingLinesRegular,
  BoxEditRegular,
  CardUiRegular,
  CheckRegular,
  CheckboxArrowRightRegular,
  SaveSyncRegular,
  SaveMultipleRegular,
  ScalesRegular,
  FormSparkleRegular,
  PersonSupportRegular,
  AppGenericRegular,
} from "@fluentui/react-icons";

import { useSignInState } from "../Components/signInState";
import { useConfig } from "../context/ConfigContext";

interface NavItemProps {
  label: string;
  icon: any;
  onClick: () => void;
  disabled?: boolean;
  isExpanded?: boolean;
  itemId?: string;
  href?: string;
  isSubItem?: boolean;
  clickedItem?: string | null;
  styles: any;
}

const NavItem = ({
  label,
  icon,
  onClick,
  disabled,
  isExpanded,
  itemId,
  href,
  isSubItem = false,
  clickedItem,
  styles,
}: NavItemProps) => {
  const location = useLocation();

  const isActiveRoute = (href: string) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(href);
  };

  const isActive = href ? isActiveRoute(href) : false;
  const isClicked = clickedItem === itemId;

  let className = styles.navItemDefault;
  if (isClicked) {
    className = styles.navItemClicked;
  } else if (isActive) {
    className = isSubItem ? styles.navItemSubActive : styles.navItemActive;
  }

  return (
    <AccordionItem
      value={label}
      disabled={disabled}
      onClick={onClick}
      className={className}
    >
      {isExpanded ? (
        <AccordionHeader icon={icon} expandIcon={null}>
          {label}
        </AccordionHeader>
      ) : (
        <Tooltip content={label} relationship="label">
          <AccordionHeader icon={icon} expandIcon={null} />
        </Tooltip>
      )}
    </AccordionItem>
  );
};

const useStyles = makeStyles({
  drawerContent: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    overflowY: "auto",
  },
  navItemClicked: {
    backgroundColor: tokens.colorNeutralBackground1Pressed,
    transform: "scale(0.98)",
    transition: "all 150ms ease-out",
    "& > div": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  navItemDefault: {
    transition: "all 150ms ease-out",
    position: "relative",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navItemActive: {
    transition: "all 150ms ease-out",
    position: "relative",
    backgroundColor: tokens.colorNeutralBackground2,

    "&::before": {
      content: '""',
      position: "absolute",
      left: "0px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "3px",
      height: "60%",
      backgroundColor: tokens.colorCompoundBrandBackground,
    },

    "& button": {
      fontWeight: tokens.fontWeightSemibold,
      color: tokens.colorCompoundBrandForeground1,

      "& > span:first-child": {
        color: tokens.colorCompoundBrandForeground1,
      },
    },

    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navItemSubActive: {
    transition: "all 150ms ease-out",
    position: "relative",
    marginLeft: "8px",
    backgroundColor: tokens.colorNeutralBackground2,

    "&::before": {
      content: '""',
      position: "absolute",
      left: "0px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "3px",
      height: "60%",
      backgroundColor: tokens.colorCompoundBrandBackground,
    },

    "& button": {
      fontWeight: tokens.fontWeightMedium,
      color: tokens.colorBrandForeground1,

      "& > span:first-child": {
        color: tokens.colorBrandForeground1,
      },
    },

    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
});

const SideNav = () => {
  const styles = useStyles();
  const { guidesUrl } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { signInState } = useSignInState();
  const [isExpanded, setIsExpanded] = useState(true);
  const [openItems, setOpenItems] = useState(["0"]);
  const [isCreateDashDisabled, setIsCreateDashDisabled] = useState(true);
  const [clickedItem, setClickedItem] = useState<string | null>(null);

  const handleAccordionToggle: AccordionToggleEventHandler<string> = (
    event,
    data
  ) => {
    setOpenItems(data.openItems);
  };

  const toggleDrawer = () => {
    setIsExpanded(!isExpanded);
  };

  const handleNavItemClick = useCallback(
    (href: string, itemId: string) => {
      // Provide visual feedback
      setClickedItem(itemId);

      // Clear the clicked state after animation
      setTimeout(() => {
        setClickedItem(null);
      }, 200);

      // Check if we're clicking on the current active route
      const isCurrentRoute =
        href === "/"
          ? location.pathname === "/"
          : location.pathname.startsWith(href);

      if (isCurrentRoute) {
        // Reload the page when clicking on the active route
        globalThis.location.reload();
      } else {
        // Navigate to different route
        navigate(href);
      }
    },
    [navigate, location.pathname]
  );

  const checkModule = useCallback(
    (moduleName: string): boolean => {
      return signInState.licenseInfo.some(
        (license) =>
          license.CustomerID === signInState.currentCustomerID &&
          license.modules.some((module) => module.name === moduleName)
      );
    },
    [
      signInState,
      signInState.userFeatures,
      signInState.currentCustomerID,
      signInState.licenseInfo,
    ]
  );

  const checkAdmin = useCallback((): boolean => {
    return signInState.userFeatures.some(
      (feature) =>
        (feature.FeatureID === 1 || feature.FeatureID === 2) &&
        feature.CustomerID === signInState.currentCustomerID &&
        feature.Enabled === true
    );
  }, [
    signInState,
    signInState.userFeatures,
    signInState.currentCustomerID,
    signInState.licenseInfo,
  ]);

  const checkReportingLicense = useCallback((): boolean => {
    return signInState.licenseInfo.some(
      (license) =>
        license.CustomerID === signInState.currentCustomerID &&
        license.IsActive === true &&
        license.modules.some((module) => module.name.startsWith("Reporting"))
    );
  }, [
    signInState,
    signInState.userFeatures,
    signInState.currentCustomerID,
    signInState.licenseInfo,
  ]);

  const checkCreateDash = useCallback((): boolean => {
    // Check if the user for the currentCustomerID has the Create Dashboards feature and it's enabled
    const feature = signInState.userFeatures.find(
      (feature) =>
        feature.FeatureID === 3 &&
        feature.CustomerID === signInState.currentCustomerID &&
        feature.Enabled === true
    );
    return Boolean(feature && checkReportingLicense());
  }, [signInState.currentCustomerID, checkReportingLicense]);

  // Auto-expand menus based on current route
  const getExpandedMenusForRoute = useCallback(() => {
    const currentPath = location.pathname.toLowerCase();
    const expandedItems: string[] = [];

    // Simple route-to-section mapping based on your existing App.tsx routes
    if (
      currentPath.startsWith("/intunedashboards") ||
      currentPath.startsWith("/customdashboards") ||
      currentPath.startsWith("/createdash")
    ) {
      expandedItems.push("reporting");
    }

    return expandedItems;
  }, [location.pathname]);

  // Update expanded menus when route or customer changes
  useEffect(() => {
    const expandedMenus = getExpandedMenusForRoute();
    setOpenItems(expandedMenus.length > 0 ? expandedMenus : []);
  }, [
    location.pathname,
    signInState.currentCustomerID,
    getExpandedMenusForRoute,
  ]);

  useEffect(() => {
    setIsCreateDashDisabled(!checkCreateDash());
  }, [
    signInState.userFeatures,
    signInState.currentCustomerID,
    signInState.licenseInfo,
  ]);

  return (
    <>
      <Button icon={<LineHorizontal3Regular />} onClick={toggleDrawer} />
      <Drawer
        open={true} // Always open
        style={{
          width: isExpanded ? "240px" : "70px",
          // transition: 'width 0.3s ease-in-out'
        }}
        size="small"
        position="start"
        type="inline"
        separator
      >
        <DrawerHeaderTitle>{isExpanded && "Navigation"}</DrawerHeaderTitle>
        <div className={styles.drawerContent}>
          {/* Add navigation links or components here */}
          <Accordion
            openItems={openItems}
            onToggle={handleAccordionToggle}
            collapsible
          >
            <NavItem
              label="Home"
              icon={<BuildingRegular />}
              onClick={() => handleNavItemClick("/", "home")}
              disabled={false}
              isExpanded={isExpanded}
              itemId="home"
              href="/"
              clickedItem={clickedItem}
              styles={styles}
            />
            <NavItem
              label="Welcome"
              icon={<AppGenericRegular />}
              onClick={() => handleNavItemClick("/welcome", "welcome")}
              disabled={false}
              isExpanded={isExpanded}
              itemId="welcome"
              href="/welcome"
              clickedItem={clickedItem}
              styles={styles}
            />
            <AccordionItem value="reporting">
              {isExpanded ? (
                <AccordionHeader
                  expandIconPosition="end"
                  icon={<ChartMultipleRegular />}
                >
                  {isExpanded && "Reporting"}
                </AccordionHeader>
              ) : (
                <Tooltip content={"Reporting"} relationship="label">
                  <AccordionHeader
                    expandIconPosition="end"
                    icon={<ChartMultipleRegular />}
                  >
                    {isExpanded && "Reporting"}
                  </AccordionHeader>
                </Tooltip>
              )}
              <AccordionPanel>
                <Accordion collapsible>
                  {checkModule("Reporting_Intune") && (
                    <NavItem
                      label="Intune Dashboards"
                      icon={<ArrowTrendingLinesRegular />}
                      onClick={() =>
                        handleNavItemClick(
                          "/intunedashboards",
                          "intune-dashboards"
                        )
                      }
                      disabled={!checkModule("Reporting_Intune")}
                      isExpanded={isExpanded}
                      itemId="intune-dashboards"
                      href="/intunedashboards"
                      isSubItem={true}
                      clickedItem={clickedItem}
                      styles={styles}
                    />
                  )}
                  <NavItem
                    label="Custom Dashboards"
                    icon={<CardUiRegular />}
                    onClick={() =>
                      handleNavItemClick(
                        "/customdashboards",
                        "custom-dashboards"
                      )
                    }
                    disabled={!checkReportingLicense()}
                    isExpanded={isExpanded}
                    itemId="custom-dashboards"
                    href="/customdashboards"
                    isSubItem={true}
                    clickedItem={clickedItem}
                    styles={styles}
                  />
                  <NavItem
                    label="Create Dashboards"
                    icon={<BoxEditRegular />}
                    onClick={() =>
                      handleNavItemClick("/createdash", "create-dashboards")
                    }
                    disabled={!checkCreateDash()}
                    isExpanded={isExpanded}
                    itemId="create-dashboards"
                    href="/createdash"
                    isSubItem={true}
                    clickedItem={clickedItem}
                    styles={styles}
                  />
                </Accordion>
              </AccordionPanel>
            </AccordionItem>
            {checkModule("Intune_Remediations") && (
              <AccordionItem value="remediationActions">
                {isExpanded ? (
                  <AccordionHeader
                    expandIconPosition="end"
                    icon={<ArrowClockwiseDashesRegular />}
                  >
                    {isExpanded && "Remediate"}
                  </AccordionHeader>
                ) : (
                  <Tooltip content={"Remediate"} relationship="label">
                    <AccordionHeader
                      expandIconPosition="end"
                      icon={<ArrowClockwiseDashesRegular />}
                    >
                      {isExpanded && "Remediate"}
                    </AccordionHeader>
                  </Tooltip>
                )}
                <AccordionPanel>
                  <Accordion collapsible>
                    <NavItem
                      label="Manage Remediations"
                      icon={<CheckRegular />}
                      onClick={() =>
                        handleNavItemClick("/", "manage-remediations")
                      }
                      disabled={!checkModule("Intune_Remediations")}
                      isExpanded={isExpanded}
                      itemId="manage-remediations"
                      isSubItem={true}
                      clickedItem={clickedItem}
                      styles={styles}
                    />
                    <NavItem
                      label="Deploy Remediations"
                      icon={<CheckboxArrowRightRegular />}
                      onClick={() =>
                        handleNavItemClick("/", "deploy-remediations")
                      }
                      disabled={!checkModule("Intune_Remediations")}
                      isExpanded={isExpanded}
                      itemId="deploy-remediations"
                      isSubItem={true}
                      clickedItem={clickedItem}
                      styles={styles}
                    />
                    <NavItem
                      label="Reporting"
                      icon={<ArrowTrendingLinesRegular />}
                      onClick={() =>
                        handleNavItemClick("/", "remediation-reporting")
                      }
                      disabled={!checkModule("Intune_Remediations")}
                      isExpanded={isExpanded}
                      itemId="remediation-reporting"
                      isSubItem={true}
                      clickedItem={clickedItem}
                      styles={styles}
                    />
                  </Accordion>
                </AccordionPanel>
              </AccordionItem>
            )}
            {(checkModule("M365LCM_Intune") || checkModule("M365LCM_CA")) && (
              <AccordionItem value="m365LifeCycle">
                {isExpanded ? (
                  <AccordionHeader
                    expandIconPosition="end"
                    icon={<ArrowSyncCheckmarkRegular />}
                  >
                    {isExpanded && "M365 LifeCycle Management"}
                  </AccordionHeader>
                ) : (
                  <Tooltip
                    content={"M365 LifeCycle Management"}
                    relationship="label"
                  >
                    <AccordionHeader
                      expandIconPosition="end"
                      icon={<ArrowSyncCheckmarkRegular />}
                    >
                      {isExpanded && "M365 LifeCycle Management"}
                    </AccordionHeader>
                  </Tooltip>
                )}
                <AccordionPanel>
                  <Accordion collapsible>
                    <NavItem
                      label="Intune Backup & Restore"
                      icon={<SaveSyncRegular />}
                      onClick={() => handleNavItemClick("/", "intune-backup")}
                      disabled={!checkModule("M365LCM_Intune")}
                      isExpanded={isExpanded}
                      itemId="intune-backup"
                      isSubItem={true}
                      clickedItem={clickedItem}
                      styles={styles}
                    />
                    <NavItem
                      label="Conditional Access Backup & Restore"
                      icon={<SaveMultipleRegular />}
                      onClick={() => handleNavItemClick("/", "ca-backup")}
                      disabled={!checkModule("M365LCM_CA")}
                      isExpanded={isExpanded}
                      itemId="ca-backup"
                      isSubItem={true}
                      clickedItem={clickedItem}
                      styles={styles}
                    />
                  </Accordion>
                </AccordionPanel>
              </AccordionItem>
            )}
            {checkModule("AVD_Base") && (
              <AccordionItem value="avdManagement">
                {isExpanded ? (
                  <AccordionHeader
                    expandIconPosition="end"
                    icon={<ViewDesktopRegular />}
                  >
                    {isExpanded && "Azure Virtual Desktop"}
                  </AccordionHeader>
                ) : (
                  <Tooltip
                    content={"Azure Virtual Desktop"}
                    relationship="label"
                  >
                    <AccordionHeader
                      expandIconPosition="end"
                      icon={<ViewDesktopRegular />}
                    >
                      {isExpanded && "Azure Virtual Desktop"}
                    </AccordionHeader>
                  </Tooltip>
                )}
                <AccordionPanel>
                  <Accordion collapsible>
                    <NavItem
                      label="Deployment"
                      icon={<FormSparkleRegular />}
                      onClick={() => handleNavItemClick("/", "avd-deployment")}
                      disabled={!checkModule("AVD_Base")}
                      isExpanded={isExpanded}
                      itemId="avd-deployment"
                      isSubItem={true}
                      clickedItem={clickedItem}
                      styles={styles}
                    />
                    <NavItem
                      label="Scaling Management"
                      icon={<ScalesRegular />}
                      onClick={() => handleNavItemClick("/", "avd-scaling")}
                      disabled={!checkModule("AVD_Base")}
                      isExpanded={isExpanded}
                      itemId="avd-scaling"
                      isSubItem={true}
                      clickedItem={clickedItem}
                      styles={styles}
                    />
                    <NavItem
                      label="Reporting"
                      icon={<ArrowTrendingLinesRegular />}
                      onClick={() => handleNavItemClick("/", "avd-reporting")}
                      disabled={!checkModule("AVD_Base")}
                      isExpanded={isExpanded}
                      itemId="avd-reporting"
                      isSubItem={true}
                      clickedItem={clickedItem}
                      styles={styles}
                    />
                  </Accordion>
                </AccordionPanel>
              </AccordionItem>
            )}

            {signInState.featureFlags?.some((flag: any) => flag.FeatureName === 'Alerts' && flag.IsEnabled) && (
              <NavItem
                label="Alert Management"
                icon={<AppGenericRegular />}
                onClick={() => handleNavItemClick("/alert-management", "alert-management")}
                disabled={false}
                isExpanded={isExpanded}
                itemId="alert-management"
                href="/alert-management"
                clickedItem={clickedItem}
                styles={styles}
              />
            )}
            <NavItem
              label="Admin Settings"
              icon={<SettingsRegular />}
              onClick={() => handleNavItemClick("/admin", "admin-settings")}
              disabled={!checkAdmin()}
              isExpanded={isExpanded}
              itemId="admin-settings"
              href="/admin"
              clickedItem={clickedItem}
              styles={styles}
            />
            <NavItem
              label="Help & Support"
              icon={<PersonSupportRegular />}
              onClick={() => {
                setClickedItem("help-support");
                setTimeout(() => setClickedItem(null), 200);
                globalThis.open(guidesUrl, "_blank");
              }}
              isExpanded={isExpanded}
              itemId="help-support"
              clickedItem={clickedItem}
              styles={styles}
            />
          </Accordion>
          {/* {isExpanded ? (
                        <Button
                            appearance='primary'
                            icon={
                                <img
                                    src={whitelogo}
                                    alt="Icon"
                                    style={{ width: "30px", height: "30px" }}
                                />
                            }
                            onClick={() => navigate('/promo')}>
                            Check out Rimo3!
                        </Button>
                    ) : (
                        <Button
                            appearance='primary'
                            icon={
                                <img
                                    src={whitelogo}
                                    alt="Icon"
                                    style={{ width: "30px", height: "30px" }}
                                />
                            }
                            onClick={() => navigate('/promo')}>
                        </Button>
                    )} */}
        </div>
      </Drawer>
    </>
  );
};

export default SideNav;
